import { motion, AnimatePresence } from 'framer-motion'
import { useState, useEffect, useRef, useCallback } from 'react'
import { useFetcher, useLocation, useParams } from 'react-router'
import { AIAssistantButton } from '#app/components/ai-assistant-button.tsx'
import { Button } from '#app/components/ui/button.tsx'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '#app/components/ui/dialog.tsx'
import {
	Drawer,
	DrawerContent,
	DrawerFooter,
} from '#app/components/ui/drawer.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Input } from '#app/components/ui/input.tsx'
import { Textarea } from '#app/components/ui/textarea.tsx'
import {
	getAudioRecorder,
	type AudioLevelData,
} from '#app/utils/audio-recorder.client.ts'
import { cn } from '#app/utils/misc.tsx'
import { getVoiceAssistant } from '#app/utils/voice.client.ts'
import { HomepageImageSelector } from './ai-homepage-image-selector.tsx'
import { AIPhotoUpload } from './ai-photo-upload.tsx'
import { SculptureSelector } from './ai-sculpture-selector.tsx'
import { CelebrationAnimation } from './celebration-animation.tsx'

// ULTIMATE VERSION: Optimistic update types
type OptimisticAction = {
	type: 'sculpture-creating' | 'photos-uploading' | 'exhibition-creating' | 'content-updating'
	message: string
	timestamp: number
}

// ULTIMATE: Custom hook for audio level visualization
function useAudioLevel(isRecording: boolean): AudioLevelData {
	const [audioData, setAudioData] = useState<AudioLevelData>({
		level: 0,
		peak: 0,
		history: [],
		isRecording: false,
	})
	const animationFrameRef = useRef<number | null>(null)

	useEffect(() => {
		if (!isRecording) {
			setAudioData({ level: 0, peak: 0, history: [], isRecording: false })
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current)
				animationFrameRef.current = null
			}
			return
		}

		const recorder = getAudioRecorder()

		const updateLevel = () => {
			if (!recorder.isRecording()) {
				setAudioData({ level: 0, peak: 0, history: [], isRecording: false })
				return
			}

			setAudioData({
				level: recorder.getAudioLevel(),
				peak: recorder.getPeakLevel(),
				history: recorder.getAudioLevelHistory(),
				isRecording: true,
			})

			animationFrameRef.current = requestAnimationFrame(updateLevel)
		}

		// Start monitoring after a small delay to ensure analyser is set up
		const timeoutId = setTimeout(() => {
			updateLevel()
		}, 100)

		return () => {
			clearTimeout(timeoutId)
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current)
			}
		}
	}, [isRecording])

	return audioData
}

type AIAction =
	| 'add-sculpture'
	| 'add-photos'
	| 'add-exhibition'
	| 'edit-about'
	| 'remove-sculpture'
	| 'view-sculpture'
	| 'close'
	| 'add-exhibition-dates'
	| 'add-exhibition-links'
	| 'view-exhibitions'
	| 'add-1-photo'
	| 'add-3-photos'
	| 'add-5-photos'
	| 'change-homepage-image'
	| 'select-position-linksboven'
	| 'select-position-rechtsboven'
	| 'select-position-linksonder'
	| 'select-position-middenonder'
	| 'select-position-rechtsonder'
	| 'select-position-centraal'
	| 'view-homepage'
	| 'add-photos-current'
	| 'remove-photo-current'
	| 'edit-text-current'
	| 'remove-sculpture-current'
	| 'edit-exhibition'
	| 'remove-exhibition'
	| 'edit-current-page'
	| 'confirm-remove-current'
	| 'ask-new-question'
	| 'edit-paragraph-yes'
	| 'edit-paragraph-no'
	| 'edit-paragraph-ai'
	| 'edit-paragraph-manual'
	| 'edit-paragraph-save-ai'
	| 'edit-paragraph-reject-ai'
	| 'edit-paragraph-save-manual'
	| 'edit-paragraph-cancel-manual'
	| 'confirm-add-exhibition'
	| 'confirm-remove-sculpture'
	| 'confirm-remove-exhibition'
	| 'edit-exhibition-title'
	| 'edit-exhibition-address'
	| 'edit-exhibition-dates'
	| 'edit-exhibition-times'
	| 'edit-exhibition-links'
	| 'edit-exhibition-description'
	| 'save-exhibition-changes'
	| 'edit-exhibition-more'
	| 'edit-sculpture'
	| 'edit-sculpture-title'
	| 'edit-sculpture-description'
	| 'edit-sculpture-category'
	| 'save-sculpture-changes'
	| 'edit-sculpture-more'
	| 'set-category-autonoom'
	| 'set-category-drijvend'
	| 'set-category-openbare-ruimte'
	| 'manage-sculpture-images'
	| 'view-sculpture-images'
	| 'delete-sculpture-image'
	| 'reorder-sculpture-image-up'
	| 'reorder-sculpture-image-down'
	| 'upload-sculpture-image'
	| 'select-image-to-delete'
	| 'confirm-delete-image'
	| 'select-image-to-reorder'
	| null

type ConversationStep = {
	id: string
	message: string
	options?: Array<{
		label: string
		action: AIAction
		imageId?: string // Optional imageId for image management
	}>
	input?: {
		type: 'text' | 'search' | 'file' | 'textarea'
		placeholder?: string
		defaultValue?: string
	}
	userResponse?: string
	sculptures?: Array<{
		id: string
		title: string
		imageUrl: string | null
	}>
	homepageImageData?: {
		currentImage: {
			id: string
			title: string
			imageUrl: string | null
		} | null
		allSculptures: Array<{
			id: string
			title: string
			imageUrl: string | null
		}>
	}
	exhibitions?: Array<{
		id: string
		title: string
		type: string
		address: string | null
		isCurrent: boolean
	}>
}

function getGreeting(userName: string = 'Else'): string {
	const hour = new Date().getHours()
	let timeGreeting = 'Goedemorgen'

	if (hour >= 12 && hour < 18) {
		timeGreeting = 'Goedemiddag'
	} else if (hour >= 18) {
		timeGreeting = 'Goedenavond'
	}

	return `${timeGreeting} ${userName}, wat kan ik voor je doen?`
}

// Calculate similarity between two strings (Levenshtein distance)
function getStringSimilarity(str1: string, str2: string): number {
	const s1 = str1.toLowerCase()
	const s2 = str2.toLowerCase()

	// If exact match, return perfect score
	if (s1 === s2) return 1

	// If one string contains the other, give high score
	if (s1.includes(s2) || s2.includes(s1)) return 0.8

	// Calculate Levenshtein distance
	const matrix: number[][] = []
	for (let i = 0; i <= s2.length; i++) {
		matrix[i] = [i]
	}
	for (let j = 0; j <= s1.length; j++) {
		if (!matrix[0]) matrix[0] = []
		matrix[0][j] = j
	}

	for (let i = 1; i <= s2.length; i++) {
		for (let j = 1; j <= s1.length; j++) {
			if (!matrix[i]) matrix[i] = []
			const prevRow = matrix[i - 1]
			if (!prevRow) continue

			if (s2.charAt(i - 1) === s1.charAt(j - 1)) {
				matrix[i]![j] = prevRow[j - 1] ?? 0
			} else {
				matrix[i]![j] = Math.min(
					(prevRow[j - 1] ?? 0) + 1, // substitution
					(matrix[i]![j - 1] ?? 0) + 1, // insertion
					(prevRow[j] ?? 0) + 1, // deletion
				)
			}
		}
	}

	const lastRow = matrix[s2.length]
	if (!lastRow) return 0
	const distance = lastRow[s1.length] ?? 0
	const maxLength = Math.max(s1.length, s2.length)
	return maxLength > 0 ? 1 - distance / maxLength : 0
}

// Default options for all pages
const defaultOptions = [
	{
		label: 'Een nieuw beeldhouwwerk toevoegen',
		action: 'add-sculpture' as AIAction,
	},
	{
		label: "Foto's toevoegen aan een beeldhouwwerk",
		action: 'add-photos' as AIAction,
	},
	{
		label: 'Een beeldhouwwerk bewerken',
		action: 'edit-sculpture' as AIAction,
	},
	{
		label: 'Een nieuwe tentoonstelling toevoegen',
		action: 'add-exhibition' as AIAction,
	},
	{
		label: 'Ik wil een beeld op de voorpagina aanpassen',
		action: 'change-homepage-image' as AIAction,
	},
]

// Get context-aware options based on current route
function getContextualOptions(
	pathname: string,
	params: Record<string, string | undefined>,
): Array<{ label: string; action: AIAction }> {
	// On beelden overview page
	if (pathname === '/beelden' || pathname === '/beelden/') {
		return [
			{
				label: 'Een nieuw beeldhouwwerk toevoegen',
				action: 'add-sculpture' as AIAction,
			},
			{
				label: "Foto's toevoegen aan een beeldhouwwerk",
				action: 'add-photos' as AIAction,
			},
			{
				label: 'Een beeldhouwwerk verwijderen',
				action: 'remove-sculpture' as AIAction,
			},
			...defaultOptions.filter(
				(opt) =>
					!['add-sculpture', 'add-photos', 'remove-sculpture'].includes(
						opt.action as string,
					),
			),
		]
	}

	// On specific beeld page
	if (pathname.startsWith('/beelden/') && params.title) {
		return [
			{
				label: "Foto's toevoegen aan dit beeld",
				action: 'add-photos-current' as AIAction,
			},
			{
				label: 'Een foto verwijderen van dit beeld',
				action: 'remove-photo-current' as AIAction,
			},
			{
				label: 'De tekst van dit beeld aanpassen',
				action: 'edit-text-current' as AIAction,
			},
			{
				label: 'Dit beeld verwijderen',
				action: 'remove-sculpture-current' as AIAction,
			},
			...defaultOptions.filter((opt) => opt.action !== 'add-photos'),
		]
	}

	// On exposities page
	if (pathname === '/exposities' || pathname === '/exposities/') {
		return [
			{
				label: 'Een nieuwe tentoonstelling toevoegen',
				action: 'add-exhibition' as AIAction,
			},
			{
				label: 'Een tentoonstelling bewerken',
				action: 'edit-exhibition' as AIAction,
			},
			{
				label: 'Een tentoonstelling verwijderen',
				action: 'remove-exhibition' as AIAction,
			},
			...defaultOptions.filter((opt) => opt.action !== 'add-exhibition'),
		]
	}

	// On homepage
	if (pathname === '/' || pathname === '') {
		return [
			{
				label: 'Ik wil een beeld op de voorpagina aanpassen',
				action: 'change-homepage-image' as AIAction,
			},
			...defaultOptions.filter((opt) => opt.action !== 'change-homepage-image'),
		]
	}

	// Default options for other pages
	return defaultOptions
}

export function AIAssistant({
	userName = 'Else',
	voiceEnabled = false,
}: {
	userName?: string
	voiceEnabled?: boolean
	voiceModel?: string
}) {
	const [isOpen, setIsOpen] = useState(false)
	const [currentStep, setCurrentStep] = useState<ConversationStep>(() => ({
		id: 'initial',
		message: getGreeting(userName),
		options: defaultOptions,
	}))
	const [conversationHistory, setConversationHistory] = useState<
		ConversationStep[]
	>([])
	const [isTyping, setIsTyping] = useState(false)
	const [inputValue, setInputValue] = useState('')
	const [isSpeaking, setIsSpeaking] = useState(false)
	const [isRecording, setIsRecording] = useState(false)
	const [, setVoiceAvailable] = useState({
		synthesis: false,
		recognition: false,
	})
	const [showCelebration, setShowCelebration] = useState(false)
	const [pendingAction, setPendingAction] = useState<AIAction | null>(null)
	const [audioFromCache, setAudioFromCache] = useState<boolean | null>(null)
	const [showTextInput, setShowTextInput] = useState(false)
	const [isSilent, setIsSilent] = useState(false)
	const silenceTimerRef = useRef<NodeJS.Timeout | null>(null)

	// ULTIMATE: Optimistic action state for immediate UI feedback
	const [optimisticAction, setOptimisticAction] = useState<OptimisticAction | null>(null)

	const location = useLocation()
	const params = useParams()

	// ULTIMATE: Use audio level hook for visualization
	const audioLevel = useAudioLevel(isRecording)

	const fetcher = useFetcher()
	const inputRef = useRef<HTMLInputElement>(null)
	const voiceAssistantRef = useRef<ReturnType<typeof getVoiceAssistant> | null>(
		null,
	)
	const audioRecorderRef = useRef<ReturnType<typeof getAudioRecorder> | null>(
		null,
	)

	// Create initial step with context-aware options
	const getInitialStep = useCallback((): ConversationStep => {
		const contextualOptions = getContextualOptions(location.pathname, params)
		return {
			id: 'initial',
			message: getGreeting(userName),
			options: contextualOptions,
		}
	}, [location.pathname, params, userName])

	// Initialize voice assistant and audio recorder
	useEffect(() => {
		if (typeof window !== 'undefined') {
			if (!voiceAssistantRef.current) {
				voiceAssistantRef.current = getVoiceAssistant()
				const availability = voiceAssistantRef.current.isAvailable()
				setVoiceAvailable(availability)
				// console.log('Voice capabilities:', availability)
			}

			if (!audioRecorderRef.current) {
				audioRecorderRef.current = getAudioRecorder()
			}
		}
	}, [])

	// Track if we should preserve state (during active processing)
	const preserveStateRef = useRef(false)

	// Reset conversation when drawer closes and manage body scroll
	useEffect(() => {
		if (!isOpen) {
			// Don't reset state if we're actively processing
			if (!preserveStateRef.current) {
				setCurrentStep(getInitialStep())
				setConversationHistory([])
				setInputValue('')
				setPendingAction(null)
				setShowTextInput(false)
			}
			// Stop any ongoing speech
			if (voiceAssistantRef.current) {
				voiceAssistantRef.current.stopSpeaking()
			}
			// Stop any ongoing recording
			if (audioRecorderRef.current && audioRecorderRef.current.isRecording()) {
				void audioRecorderRef.current.stopRecording()
			}
			setIsSpeaking(false)
			setIsRecording(false)
			// Re-enable body scroll
			if (typeof document !== 'undefined') {
				document.body.style.overflow = ''
			}
		} else if (isOpen) {
			// Reset previous step ref to ensure initial greeting plays
			if (previousStepRef.current === null || currentStep.id === 'initial') {
				previousStepRef.current = null
			}
			// Disable body scroll
			if (typeof document !== 'undefined') {
				document.body.style.overflow = 'hidden'
			}
		}

		// Cleanup function to ensure scroll is re-enabled if component unmounts
		return () => {
			if (typeof document !== 'undefined') {
				document.body.style.overflow = ''
			}
		}
	}, [isOpen, getInitialStep, currentStep.id])

	// Function to play speech using OpenAI TTS
	const playOpenAISpeech = useCallback(
		async (text: string, onComplete?: () => void) => {
			if (!voiceEnabled) {
				onComplete?.()
				return
			}

			setIsSpeaking(true)
			try {
				// Request TTS from server (server will check cache first)
				const response = await fetch('/resources/ai-assistant', {
					method: 'POST',
					headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
					body: new URLSearchParams({
						intent: 'generate-speech',
						text,
						voiceEnabled: 'true',
					}),
				})

				if (response.ok) {
					// Check if audio was from cache (only in development)
					const fromCache =
						response.headers.get('X-Audio-From-Cache') === 'true'
					// Check if we're in development mode (localhost)
					if (
						typeof window !== 'undefined' &&
						(window.location.hostname === 'localhost' ||
							window.location.hostname === '127.0.0.1')
					) {
						setAudioFromCache(fromCache)
					}

					const audioBlob = await response.blob()
					const audio = new Audio(URL.createObjectURL(audioBlob))

					audio.onended = () => {
						setIsSpeaking(false)
						// Clear cache indicator after audio ends
						setTimeout(() => setAudioFromCache(null), 1000)
						onComplete?.()
					}

					audio.onerror = () => {
						setIsSpeaking(false)
						setAudioFromCache(null)
						console.error('Failed to play audio')
						onComplete?.()
					}

					await audio.play()
				} else {
					setIsSpeaking(false)
					setAudioFromCache(null)
					console.error('Failed to generate speech')
					onComplete?.()
				}
			} catch (error) {
				setIsSpeaking(false)
				console.error('Error playing speech:', error)
				onComplete?.()
			}
		},
		[voiceEnabled],
	)

	// Track previous step to avoid re-animations
	const previousStepRef = useRef<string | null>(null)

	// Simulate typing animation and speak messages
	useEffect(() => {
		// Only trigger if the step actually changed (or on first render)
		if (
			currentStep.message &&
			(previousStepRef.current === null ||
				currentStep.id !== previousStepRef.current)
		) {
			previousStepRef.current = currentStep.id
			setIsTyping(true)
			const timer = setTimeout(() => {
				setIsTyping(false)

				// Use OpenAI TTS if voice is enabled
				if (voiceEnabled && isOpen) {
					// Small delay to ensure drawer is fully open
					setTimeout(() => {
						void playOpenAISpeech(currentStep.message, () => {
							// If there's a pending action AND no options (not a yes/no question), execute it
							if (pendingAction && !currentStep.options) {
								handleOptionClick(pendingAction)
								setPendingAction(null)
							}
						})
					}, 100)
				} else if (pendingAction && !currentStep.options) {
					// If voice is not available and no options, wait a bit then execute the pending action
					setTimeout(() => {
						handleOptionClick(pendingAction)
						setPendingAction(null)
					}, 1500)
				}
			}, 800)
			return () => clearTimeout(timer)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentStep.message, currentStep.id, isOpen, pendingAction, voiceEnabled])

	// Handle AI response
	useEffect(() => {
		if (fetcher.data && fetcher.state === 'idle') {
			// ULTIMATE: Clear optimistic action when response arrives
			setOptimisticAction(null)

			// Handle transcription response
			if ('transcript' in fetcher.data) {
				const { transcript } = fetcher.data as { transcript: string }
				// console.log('Received transcript:', transcript)
				setIsTyping(false)

				if (transcript.trim()) {
					// Check if we're in sculpture search mode
					if (currentStep.sculptures) {
						// Use transcript for sculpture search
						const searchTerm = transcript.toLowerCase().trim()

						// First try exact match
						const exactMatch = currentStep.sculptures.find((s) =>
							s.title.toLowerCase().includes(searchTerm),
						)

						if (exactMatch) {
							// If exact match found, use the search term
							// Force immediate update by clearing first
							setTimeout(() => {}, 0)
						} else {
							// Find closest match using fuzzy search
							let bestMatch = { sculpture: null as any, score: 0 }

							for (const sculpture of currentStep.sculptures) {
								const similarity = getStringSimilarity(
									searchTerm,
									sculpture.title,
								)
								if (similarity > bestMatch.score) {
									bestMatch = { sculpture, score: similarity }
								}
							}

							// If we found a reasonable match (>40% similarity), select it directly
							if (bestMatch.score > 0.4 && bestMatch.sculpture) {
								// Auto-select the best match
								setActionData((prev) => ({
									...prev,
									sculptureId: bestMatch.sculpture.id,
									sculptureTitle: bestMatch.sculpture.title,
								}))
								setCurrentStep({
									id: 'photos-for-sculpture',
									message: `Ik denk dat je "${bestMatch.sculpture.title}" bedoelt. Hoeveel foto's wil je toevoegen?`,
									options: [
										{ label: '1 foto', action: 'add-1-photo' },
										{ label: "3 foto's", action: 'add-3-photos' },
										{ label: "5 foto's", action: 'add-5-photos' },
									],
								})
							} else {
								// No good match found, still use as search term
								// Force immediate update by clearing first
								setTimeout(() => {}, 0)
							}
						}
					} else if (
						currentStep.id === 'edit-paragraph-what' ||
						currentStep.id === 'edit-paragraph-confirm-yes'
					) {
						// We're in paragraph editing mode, use the transcript as edit instructions
						setActionData((prev) => ({ ...prev, editInstructions: transcript }))
						// Use AI to generate the new paragraph
						void fetcher.submit(
							{
								intent: 'ai-edit-paragraph',
								sectionId: actionData.sectionId,
								originalContent: actionData.originalContent,
								editInstructions: transcript,
							},
							{
								method: 'POST',
								action: '/resources/ai-assistant',
							},
						)
						setIsTyping(true)
					} else {
						// Set the transcript as input value and auto-submit
						setInputValue(transcript)

						// Submit the transcribed text for analysis immediately
						void fetcher.submit(
							{
								intent: 'analyze',
								message: transcript,
							},
							{
								method: 'POST',
								action: '/resources/ai-assistant',
							},
						)
						setInputValue('')
						setIsTyping(true)
					}
				}
			}
			// Handle action execution response
			else if ('step' in fetcher.data && fetcher.data.step) {
				setIsTyping(false)
				// Action completed with a new step
				setCurrentStep(fetcher.data.step)
				// Update action data after successful action
				if ('success' in fetcher.data && fetcher.data.success) {
					// Store sculptureId if it was just created
					if ('sculptureId' in fetcher.data && fetcher.data.sculptureId) {
						setActionData((prev) => ({
							...prev,
							sculptureId: fetcher.data.sculptureId,
							sculptureTitle:
								fetcher.data.step.message.match(/"([^"]+)"/)?.[1] ||
								'het beeldhouwwerk',
						}))
					}
					// Only clear photo-related data if it was a photo upload
					else if (fetcher.data.step.id === 'photos-uploaded') {
						setActionData((prev) => {
							const { photoCount, ...rest } = prev
							return rest
						})
					}
					// Show celebration animation
					setShowCelebration(true)
					// Reset preserve state flag after success
					preserveStateRef.current = false
				}
			}
			// Handle errors
			else if ('error' in fetcher.data) {
				setIsTyping(false)
				setCurrentStep({
					id: 'error',
					message: `Er ging iets mis: ${fetcher.data.error}. Probeer het opnieuw.`,
					options: [
						{ label: '🔄 Opnieuw proberen', action: 'add-photos' },
						{ label: '🏠 Terug naar menu', action: 'close' },
					],
				})
			}
			// Handle sculpture search results
			else if ('sculptures' in fetcher.data && 'step' in fetcher.data) {
				setIsTyping(false)
				const step = fetcher.data.step as ConversationStep
				// Add sculptures to the step if present
				if (fetcher.data.sculptures && fetcher.data.sculptures.length > 0) {
					step.sculptures = fetcher.data.sculptures
					// Update message based on mode
					if (actionData.removeMode) {
						step.message =
							'Welk beeldhouwwerk wil je verwijderen? Selecteer een beeld uit de lijst:'
						step.id = 'select-sculpture-to-remove'
					} else if (actionData.editSculptureMode) {
						step.message =
							'Welk beeldhouwwerk wil je bewerken? Selecteer een beeld uit de lijst:'
						step.id = 'select-sculpture-to-edit'
					}
				}
				setCurrentStep(step)
			}
			// Handle exhibition search results
			else if ('exhibitions' in fetcher.data && fetcher.data.exhibitions) {
				setIsTyping(false)
				const exhibitions = fetcher.data.exhibitions
				if (exhibitions.length > 0) {
					// Store the first exhibition as selected
					setActionData((prev) => ({
						...prev,
						selectedExhibition: exhibitions[0],
					}))
					// Show preview of the found exhibition
					const exhibition = exhibitions[0]
					setCurrentStep({
						id: 'exhibition-preview',
						message: `Ik heb de volgende ${exhibition.type === 'gallery' ? 'galerie' : 'tentoonstelling'} gevonden:\n\n**${exhibition.title}**\n${exhibition.address ? `📍 ${exhibition.address}\n` : ''}${exhibition.beginDate ? `📅 ${exhibition.beginDate}${exhibition.endDate ? ` tot ${exhibition.endDate}` : ''}\n` : ''}${exhibition.description ? `\n${exhibition.description}\n` : ''}\nWil je deze toevoegen aan je website?`,
						options: [
							{ label: 'Ja', action: 'confirm-add-exhibition' as AIAction },
							{ label: 'Nee', action: 'close' as AIAction },
						],
					})
				} else {
					setCurrentStep({
						id: 'exhibition-not-found',
						message:
							'Ik kon geen tentoonstelling of galerie vinden met die informatie. Wil je het opnieuw proberen?',
						options: [
							{
								label: 'Ja, opnieuw proberen',
								action: 'add-exhibition' as AIAction,
							},
							{ label: 'Nee, terug naar menu', action: 'close' as AIAction },
						],
					})
				}
			}
			// Handle homepage image selection data
			else if ('homepageImageData' in fetcher.data && 'step' in fetcher.data) {
				setIsTyping(false)
				const step = fetcher.data.step as ConversationStep
				step.homepageImageData = fetcher.data.homepageImageData
				// Also ensure we store the position data for the selector
				if (
					!actionData.positionName &&
					actionData.positionIndex !== undefined
				) {
					const positionNames = [
						'linksboven',
						'rechtsboven',
						'linksonder',
						'middenonder',
						'rechtsonder',
						'het centrale beeld',
					]
					setActionData((prev) => ({
						...prev,
						positionName: positionNames[actionData.positionIndex] || '',
					}))
				}
				setCurrentStep(step)
			}
			// Handle exhibitions list
			else if (
				'exhibitionsList' in fetcher.data &&
				fetcher.data.exhibitionsList
			) {
				setIsTyping(false)
				const exhibitions = fetcher.data.exhibitionsList
				if (exhibitions.length > 0) {
					const message = actionData.editExhibitionMode
						? 'Welke tentoonstelling wil je bewerken?'
						: 'Welke tentoonstelling wil je verwijderen?'

					setCurrentStep({
						id: actionData.editExhibitionMode
							? 'select-exhibition-to-edit'
							: 'select-exhibition-to-remove',
						message: message,
						exhibitions: exhibitions,
					})
				} else {
					setCurrentStep({
						id: 'no-exhibitions',
						message: 'Er zijn nog geen tentoonstellingen toegevoegd.',
						options: [
							{
								label: '➕ Tentoonstelling toevoegen',
								action: 'add-exhibition' as AIAction,
							},
							{ label: '🏠 Terug naar menu', action: 'close' as AIAction },
						],
					})
				}
			}
			// Handle exhibition details for editing
			else if ('exhibitionDetails' in fetcher.data && 'step' in fetcher.data) {
				setIsTyping(false)
				const { exhibitionDetails } = fetcher.data
				// Store exhibition details for editing
				setActionData((prev) => ({
					...prev,
					editingExhibition: exhibitionDetails,
					exhibitionUpdates: {},
				}))
				setCurrentStep(fetcher.data.step)
			}
			// Handle exhibition field update
			else if ('fieldUpdated' in fetcher.data && fetcher.data.fieldUpdated) {
				setIsTyping(false)
				// Check if it's a sculpture or exhibition update
				if (fetcher.data.step.id === 'sculpture-field-updated') {
					// Store the sculpture field update
					setActionData((prev) => ({
						...prev,
						sculptureUpdates: {
							...prev.sculptureUpdates,
							[fetcher.data.field]: fetcher.data.value,
						},
					}))
				} else {
					// Store the exhibition field update
					setActionData((prev) => ({
						...prev,
						exhibitionUpdates: {
							...prev.exhibitionUpdates,
							[fetcher.data.field]: fetcher.data.value,
						},
					}))
				}
				setCurrentStep(fetcher.data.step)
			}
			// Handle sculpture details for editing
			else if ('sculptureDetails' in fetcher.data && 'step' in fetcher.data) {
				setIsTyping(false)
				const { sculptureDetails } = fetcher.data
				// Store sculpture details for editing
				setActionData((prev) => ({
					...prev,
					editingSculpture: sculptureDetails,
					sculptureUpdates: {},
				}))
				setCurrentStep(fetcher.data.step)
			}
			// Handle sculpture images response
			else if ('sculptureImages' in fetcher.data && 'step' in fetcher.data) {
				setIsTyping(false)
				const { sculptureImages } = fetcher.data
				// Store images for management
				setActionData((prev) => ({
					...prev,
					sculptureImages: sculptureImages,
				}))
				setCurrentStep(fetcher.data.step)
			}
			// Handle AI paragraph editing response
			else if ('aiEditedContent' in fetcher.data) {
				setIsTyping(false)
				const { aiEditedContent } = fetcher.data as { aiEditedContent: string }
				// Store the AI-edited content
				setActionData((prev) => ({ ...prev, aiEditedContent }))
				// Show the edited content with options
				setCurrentStep({
					id: 'edit-paragraph-confirm-ai',
					message: `Dit is de aangepaste tekst:\n\n"${aiEditedContent}"\n\nWil je deze aangepaste tekst plaatsen?`,
					options: [
						{ label: 'Ja', action: 'edit-paragraph-save-ai' },
						{ label: 'Nee', action: 'edit-paragraph-reject-ai' },
					],
				})
			}
			// Handle first paragraph response for edit-about
			else if (
				'firstParagraph' in fetcher.data &&
				fetcher.data.firstParagraph
			) {
				setIsTyping(false)
				const { sectionId, content } = fetcher.data.firstParagraph as {
					sectionId: string
					content: string
				}
				// Store the paragraph data
				setActionData({ sectionId, originalContent: content })
				// Ask for confirmation
				setCurrentStep({
					id: 'edit-paragraph-confirm',
					message: `Dit is de eerste paragraaf van je Over mij pagina:\n\n"${content}"\n\nWil je deze paragraaf bewerken?`,
					options: [
						{
							label: '✏️ Geef instructies voor aanpassing',
							action: 'edit-paragraph-yes',
						},
						{
							label: '📝 Direct tekst bewerken',
							action: 'edit-paragraph-manual',
						},
						{ label: 'Nee, annuleren', action: 'edit-paragraph-no' },
					],
				})
			}
			// Handle paragraph update response
			else if (
				'paragraphUpdated' in fetcher.data &&
				fetcher.data.paragraphUpdated
			) {
				setIsTyping(false)
				// Show success message
				setCurrentStep({
					id: 'edit-paragraph-success',
					message: 'De tekst is succesvol bijgewerkt!',
					options: [{ label: '🏠 Terug naar menu', action: 'close' }],
				})
				// Dispatch event to update the paragraph on the page
				const event = new CustomEvent('ai-paragraph-update', {
					detail: {
						sectionId: actionData.sectionId,
						content: fetcher.data.content,
					},
				})
				window.dispatchEvent(event)
				// Show celebration
				setShowCelebration(true)
			}
			// Handle analysis response
			else if ('action' in fetcher.data && 'response' in fetcher.data) {
				// Analyzed intent response
				const { action, response, exhibitionName } = fetcher.data as {
					action: AIAction | null
					response: string
					exhibitionName?: string
				}

				// Reset preserve state flag
				preserveStateRef.current = false

				// Stop any ongoing speech before transitioning
				if (voiceAssistantRef.current) {
					voiceAssistantRef.current.stopSpeaking()
				}
				setIsSpeaking(false)

				// Check if the response contains a yes/no question
				const isYesNoQuestion =
					response.includes('?') &&
					(response.toLowerCase().includes('wil je') ||
						response.toLowerCase().includes('wilt u') ||
						response.toLowerCase().includes('ga je') ||
						response.toLowerCase().includes('gaat u') ||
						response.toLowerCase().includes('zullen we') ||
						response.toLowerCase().includes('klopt dat') ||
						response.toLowerCase().includes('is dat correct') ||
						response.toLowerCase().includes('akkoord') ||
						response.toLowerCase().includes('zou je'))

				// Check if this is asking about a sculpture name
				const isAskingAboutSculptureName =
					response.toLowerCase().includes('beeldhouwwerk') &&
					response.includes('?') &&
					(response.toLowerCase().includes('genaamd') ||
						response.toLowerCase().includes('heet') ||
						response.toLowerCase().includes('naam'))

				if (action) {
					// If user directly mentioned an exhibition name, search for it immediately
					if (action === 'add-exhibition' && exhibitionName) {
						setCurrentStep({
							id: 'ai-response',
							message: response,
						})
						// Store the exhibition name and trigger search after showing message
						setActionData((prev) => ({ ...prev, exhibitionName }))
						setTimeout(() => {
							handleExhibitionFlow('add-exhibition-search', exhibitionName)
						}, 1500)
					} else {
						// Store the pending action to execute after speech completes
						setPendingAction(action)
						// Show AI response and wait for speech to complete
						setCurrentStep({
							id: 'ai-response',
							message: response,
							// Add yes/no options if it's a confirmation question
							options: isYesNoQuestion
								? [
										{ label: 'Ja', action: action },
										{ label: 'Nee', action: 'ask-new-question' as AIAction },
									]
								: undefined,
						})
					}
				} else if (isAskingAboutSculptureName) {
					// Extract the sculpture name from the response
					const nameMatch = response.match(
						/"([^"]+)"|'([^']+)'|genaamd\s+(\S+)|naam\s+(\S+)/i,
					)
					const sculptureNameGuess =
						nameMatch?.[1] ||
						nameMatch?.[2] ||
						nameMatch?.[3] ||
						nameMatch?.[4] ||
						''

					// Store the guessed name
					if (sculptureNameGuess) {
						setActionData((prev) => ({
							...prev,
							guessedSculptureName: sculptureNameGuess,
						}))
					}

					// Handle sculpture name confirmation
					setCurrentStep({
						id: 'confirm-sculpture-name',
						message: response,
						options: [
							{ label: 'Ja', action: 'add-sculpture' },
							{
								label: 'Ik wil de naam opnieuw inspreken',
								action: 'ask-new-question' as AIAction,
							},
						],
					})
				} else {
					// Just show the response
					setCurrentStep({
						id: 'ai-response-continue',
						message: response,
					})
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fetcher.data, fetcher.state])

	// Store action data as we collect it
	const [actionData, setActionData] = useState<Record<string, any>>({})
	const [showExitConfirmation, setShowExitConfirmation] = useState(false)

	// Listen for paragraph edit events from the Over page
	useEffect(() => {
		const handleParagraphEdit = (event: CustomEvent) => {
			const { sectionId, content } = event.detail

			// Open the AI assistant if not already open
			setIsOpen(true)

			// Store the paragraph data
			setActionData({ sectionId, originalContent: content })

			// Start the edit flow
			setCurrentStep({
				id: 'edit-paragraph-confirm',
				message: 'Wil je deze paragraaf bewerken?',
				options: [
					{ label: 'Ja', action: 'edit-paragraph-yes' },
					{ label: 'Nee', action: 'edit-paragraph-no' },
				],
			})
		}

		window.addEventListener(
			'trigger-ai-edit-paragraph' as any,
			handleParagraphEdit,
		)
		return () => {
			window.removeEventListener(
				'trigger-ai-edit-paragraph' as any,
				handleParagraphEdit,
			)
		}
	}, [])

	const handleSculptureFlow = (stepId: string, value: string) => {
		switch (stepId) {
			case 'add-sculpture-title':
				setActionData((prev) => ({ ...prev, title: value }))
				setCurrentStep({
					id: 'add-sculpture-description',
					message: 'Geef een beschrijving van het beeldhouwwerk.',
					input: {
						type: 'text',
						placeholder: 'Voer een beschrijving in...',
					},
				})
				break
			case 'add-sculpture-description':
				// ULTIMATE: Set optimistic action for immediate feedback
				setOptimisticAction({
					type: 'sculpture-creating',
					message: `Beeldhouwwerk "${actionData.title}" wordt aangemaakt...`,
					timestamp: Date.now(),
				})
				// Execute the action to create the sculpture
				void fetcher.submit(
					{
						intent: 'create-sculpture',
						title: actionData.title,
						description: value,
					},
					{
						method: 'POST',
						action: '/resources/ai-assistant',
					},
				)
				break
		}
	}

	const handlePhotosFlow = (stepId: string, value: string) => {
		switch (stepId) {
			case 'add-photos-search':
				// Search for sculptures
				void fetcher.submit(
					{
						intent: 'search-sculptures',
						query: value,
					},
					{
						method: 'POST',
						action: '/resources/ai-assistant',
					},
				)
				break
		}
	}

	const handleExhibitionFlow = (stepId: string, value: string) => {
		switch (stepId) {
			case 'add-exhibition-search':
				// Search for exhibitions using OpenAI
				void fetcher.submit(
					{
						intent: 'search-exhibitions',
						query: value,
					},
					{
						method: 'POST',
						action: '/resources/ai-assistant',
					},
				)
				setIsTyping(true)
				break
			case 'confirm-add-exhibition':
				// Create the exhibition
				const exhibition = actionData.selectedExhibition
				if (exhibition) {
					// ULTIMATE: Set optimistic action for immediate feedback
					setOptimisticAction({
						type: 'exhibition-creating',
						message: `${exhibition.type === 'gallery' ? 'Galerie' : 'Tentoonstelling'} "${exhibition.title}" wordt toegevoegd...`,
						timestamp: Date.now(),
					})
					void fetcher.submit(
						{
							intent: 'create-exhibition',
							title: exhibition.title,
							type: exhibition.type,
							address: exhibition.address || '',
							beginDate: exhibition.beginDate || '',
							endDate: exhibition.endDate || '',
							openingTimes: JSON.stringify(exhibition.openingTimes || []),
							googleMapsUrl: exhibition.googleMapsUrl || '',
							websiteUrl: exhibition.websiteUrl || '',
							description: exhibition.description || '',
						},
						{
							method: 'POST',
							action: '/resources/ai-assistant',
						},
					)
					setIsTyping(true)
				}
				break
		}
	}

	// Check if current workflow has unsaved data
	const hasUnsavedData = () => {
		// Check if we're in a workflow with data
		if (currentStep.id?.includes('add-sculpture-') && (actionData.title || actionData.content)) {
			return true
		}
		if (currentStep.id?.includes('add-exhibition-') && actionData.selectedExhibition) {
			return true
		}
		if (currentStep.id?.includes('edit-exhibition-') && actionData.exhibitionUpdates && Object.keys(actionData.exhibitionUpdates).length > 0) {
			return true
		}
		if (currentStep.id?.includes('edit-sculpture-') && actionData.sculptureUpdates && Object.keys(actionData.sculptureUpdates).length > 0) {
			return true
		}
		if (currentStep.id?.includes('edit-paragraph-') && (actionData.editInstructions || actionData.aiEditedContent)) {
			return true
		}
		if (currentStep.id === 'upload-photos' && actionData.photoCount) {
			return true
		}
		// Check for active modes
		if (actionData.removeMode || actionData.editExhibitionMode || actionData.editSculptureMode) {
			return true
		}
		return false
	}

	// Actions that don't need confirmation
	const safeActions: AIAction[] = [
		'view-sculpture',
		'view-exhibitions', 
		'view-homepage',
		'ask-new-question',
		'close',
	]

	// Check if action needs confirmation
	const needsConfirmation = (newAction: AIAction) => {
		// Don't need confirmation if no unsaved data
		if (!hasUnsavedData()) return false
		
		// Don't need confirmation for safe actions
		if (newAction && safeActions.includes(newAction)) return false
		
		// Don't need confirmation if continuing same workflow
		if (newAction && currentStep.id?.includes('sculpture') && newAction.includes('sculpture')) {
			return false
		}
		if (newAction && currentStep.id?.includes('exhibition') && newAction.includes('exhibition')) {
			return false
		}
		
		return true
	}

	const handleOptionClick = (action: AIAction, imageId?: string) => {
		// Stop any ongoing speech when navigating
		if (voiceAssistantRef.current) {
			voiceAssistantRef.current.stopSpeaking()
		}
		setIsSpeaking(false)
		
		// Check if we need confirmation before switching actions
		if (needsConfirmation(action)) {
			setPendingAction(action)
			setShowExitConfirmation(true)
			return
		}

		// Clear pending action to avoid double execution
		setPendingAction(null)

		// Store current step in history
		setConversationHistory((prev) => [...prev, currentStep])
		
		// Handle image selection actions specially
		if (action === 'select-image-to-delete' && imageId) {
			setActionData((prev) => ({ ...prev, selectedImageId: imageId }))
			setCurrentStep({
				id: 'confirm-delete-image',
				message: 'Weet je zeker dat je deze afbeelding wilt verwijderen? Dit kan niet ongedaan worden gemaakt.',
				options: [
					{ label: 'Ja, verwijder', action: 'confirm-delete-image' },
					{ label: 'Nee, annuleer', action: 'manage-sculpture-images' },
				],
			})
			return
		}
		
		if (action === 'select-image-to-reorder' && imageId) {
			setActionData((prev) => ({ ...prev, selectedImageId: imageId }))
			// Proceed with reordering
			void fetcher.submit(
				{
					intent: 'reorder-sculpture-image',
					imageId: imageId,
					direction: actionData.reorderDirection,
				},
				{
					method: 'POST',
					action: '/resources/ai-assistant',
				},
			)
			setIsTyping(true)
			return
		}

		// Handle different actions
		switch (action) {
			case 'add-sculpture':
				setCurrentStep({
					id: 'add-sculpture-title',
					message: 'Wat is de titel van het nieuwe beeldhouwwerk?',
					input: {
						type: 'text',
						placeholder: 'Voer de titel in...',
					},
				})
				break
			case 'add-photos':
				// First, let's fetch all sculptures to show
				void fetcher.submit(
					{
						intent: 'search-sculptures',
						query: '', // Empty query to get all sculptures
					},
					{
						method: 'POST',
						action: '/resources/ai-assistant',
					},
				)
				setIsTyping(true)
				break
			case 'add-exhibition':
				setCurrentStep({
					id: 'add-exhibition-search',
					message:
						'Geef me wat informatie over de tentoonstelling of galerie die je wilt toevoegen.',
					input: {
						type: 'text',
						placeholder:
							'bijv. Stedelijk Museum Amsterdam of naam tentoonstelling...',
					},
				})
				break
			case 'edit-about':
				// Fetch the first paragraph of the Over page
				void fetcher.submit(
					{
						intent: 'get-first-paragraph',
					},
					{
						method: 'POST',
						action: '/resources/ai-assistant',
					},
				)
				setIsTyping(true)
				break
			case 'remove-sculpture':
				// First, fetch all sculptures to show
				void fetcher.submit(
					{
						intent: 'search-sculptures',
						query: '', // Empty query to get all sculptures
					},
					{
						method: 'POST',
						action: '/resources/ai-assistant',
					},
				)
				setIsTyping(true)
				// Set a flag to indicate we're in remove mode
				setActionData((prev) => ({ ...prev, removeMode: true }))
				break
			case 'close':
				// Close the drawer
				setIsOpen(false)
				break
			case 'view-sculpture':
				// Navigate to the specific sculpture if we have its ID, otherwise to the gallery
				if (actionData.sculptureId && actionData.sculptureTitle) {
					const slug = actionData.sculptureTitle
						.toLowerCase()
						.replace(/[^a-z0-9]+/g, '-')
						.replace(/^-+|-+$/g, '')
					window.location.href = `/beelden/${slug}`
				} else {
					window.location.href = '/beelden'
				}
				break
			case 'view-exhibitions':
				// Navigate to exhibitions page
				window.location.href = '/exposities'
				break
			case 'add-exhibition-dates':
				setCurrentStep({
					id: 'exhibition-dates',
					message:
						'Wanneer vindt de tentoonstelling plaats? Geef de begin- en einddatum.',
					input: {
						type: 'text',
						placeholder: 'bijv. 15 maart - 20 april 2024',
					},
				})
				break
			case 'add-exhibition-links':
				setCurrentStep({
					id: 'exhibition-links',
					message: 'Heeft de tentoonstelling een website? Voer de URL in.',
					input: {
						type: 'text',
						placeholder: 'https://...',
					},
				})
				break
			case 'add-1-photo':
			case 'add-3-photos':
			case 'add-5-photos':
				const photoCount =
					action === 'add-1-photo' ? 1 : action === 'add-3-photos' ? 3 : 5
				setCurrentStep({
					id: 'upload-photos',
					message: `Perfect! Je kunt nu ${photoCount} foto${photoCount > 1 ? "'s" : ''} selecteren voor "${actionData.sculptureTitle || 'het beeldhouwwerk'}".`,
					input: {
						type: 'file',
						placeholder: `Selecteer ${photoCount} foto${photoCount > 1 ? "'s" : ''}`,
					},
				})
				// Store the photo count for later use
				setActionData((prev) => ({ ...prev, photoCount }))
				break
			case 'change-homepage-image':
				setCurrentStep({
					id: 'select-homepage-position',
					message:
						'De voorpagina bestaat uit 5 fragmenten en één centraal beeld in het midden getoond achter de woorden Fragments of Homo Ludens. Welk beeld wil je vervangen?',
					options: [
						{ label: 'Linksboven', action: 'select-position-linksboven' },
						{ label: 'Rechtsboven', action: 'select-position-rechtsboven' },
						{ label: 'Linksonder', action: 'select-position-linksonder' },
						{ label: 'Middenonder', action: 'select-position-middenonder' },
						{ label: 'Rechtsonder', action: 'select-position-rechtsonder' },
						{ label: 'Het centrale beeld', action: 'select-position-centraal' },
					],
				})
				break
			case 'select-position-linksboven':
			case 'select-position-rechtsboven':
			case 'select-position-linksonder':
			case 'select-position-middenonder':
			case 'select-position-rechtsonder':
			case 'select-position-centraal':
				// Map action to position index
				const positionMap = {
					'select-position-linksboven': 0,
					'select-position-rechtsboven': 1,
					'select-position-linksonder': 2,
					'select-position-middenonder': 3,
					'select-position-rechtsonder': 4,
					'select-position-centraal': 5,
				}
				const positionIndex = positionMap[action]
				const positionName = action
					.replace('select-position-', '')
					.replace('-', ' ')

				setActionData((prev) => ({ ...prev, positionIndex, positionName }))

				// Fetch current featured images and all sculptures
				void fetcher.submit(
					{
						intent: 'get-homepage-images',
						positionIndex: positionIndex.toString(),
					},
					{
						method: 'POST',
						action: '/resources/ai-assistant',
					},
				)
				setIsTyping(true)
				break
			case 'view-homepage':
				// Navigate to homepage
				window.location.href = '/'
				break
			case 'add-photos-current':
				// Get current sculpture from URL
				const currentSculptureTitle = params.title || ''
				setActionData((prev) => ({ ...prev, currentSculptureTitle }))
				// Search for the current sculpture
				void fetcher.submit(
					{
						intent: 'search-sculptures',
						query: currentSculptureTitle.replace(/-/g, ' '),
					},
					{
						method: 'POST',
						action: '/resources/ai-assistant',
					},
				)
				setIsTyping(true)
				break
			case 'remove-photo-current':
				setCurrentStep({
					id: 'remove-photo-current',
					message:
						"Deze functie komt binnenkort beschikbaar. Voor nu kun je foto's verwijderen via de beheerpagina.",
					options: [{ label: '🔙 Terug naar opties', action: 'close' }],
				})
				break
			case 'edit-text-current':
				setCurrentStep({
					id: 'edit-text-current',
					message:
						'Om de tekst van dit beeld aan te passen, ga naar de bewerkpagina.',
					options: [
						{ label: '✏️ Ga naar bewerkpagina', action: 'edit-current-page' },
						{ label: '🔙 Terug naar opties', action: 'close' },
					],
				})
				break
			case 'edit-current-page':
				// Navigate to edit page for current sculpture
				if (params.title) {
					window.location.href = `/beelden/${params.title}/edit`
				}
				break
			case 'remove-sculpture-current':
				setCurrentStep({
					id: 'confirm-remove-current',
					message:
						'Weet je zeker dat je dit beeld wilt verwijderen? Dit kan niet ongedaan worden gemaakt.',
					options: [
						{
							label: '🗑️ Ja, verwijder dit beeld',
							action: 'confirm-remove-current',
						},
						{ label: '❌ Nee, annuleren', action: 'close' },
					],
				})
				break
			case 'edit-exhibition':
				// Fetch all exhibitions to show
				void fetcher.submit(
					{
						intent: 'get-exhibitions',
					},
					{
						method: 'POST',
						action: '/resources/ai-assistant',
					},
				)
				setIsTyping(true)
				// Set a flag to indicate we're in edit mode
				setActionData((prev) => ({ ...prev, editExhibitionMode: true }))
				break
			case 'remove-exhibition':
				// Fetch all exhibitions to show
				void fetcher.submit(
					{
						intent: 'get-exhibitions',
					},
					{
						method: 'POST',
						action: '/resources/ai-assistant',
					},
				)
				setIsTyping(true)
				// Set a flag to indicate we're in remove mode
				setActionData((prev) => ({ ...prev, removeExhibitionMode: true }))
				break
			case 'ask-new-question':
				setCurrentStep({
					id: 'new-question',
					message:
						'Waarmee kan ik je dan van dienst mee zijn? Je mag me alles vragen en ik help je graag!',
					options: getContextualOptions(location.pathname, params),
				})
				break
			case 'edit-paragraph-yes':
				setCurrentStep({
					id: 'edit-paragraph-confirm-yes',
					message:
						'Wat wil je veranderen? Geef me de nieuwe tekst of beschrijf wat je wilt aanpassen.',
					input: {
						type: 'text',
						placeholder:
							'Beschrijf wat je wilt aanpassen of dicteer de nieuwe tekst...',
					},
				})
				break
			case 'edit-paragraph-no':
				// Close the assistant
				setIsOpen(false)
				break
			case 'edit-paragraph-ai':
				// Use AI to rewrite the paragraph
				void fetcher.submit(
					{
						intent: 'ai-edit-paragraph',
						sectionId: actionData.sectionId,
						originalContent: actionData.originalContent,
						editInstructions: actionData.editInstructions,
					},
					{
						method: 'POST',
						action: '/resources/ai-assistant',
					},
				)
				setIsTyping(true)
				break
			case 'edit-paragraph-manual':
				// Show manual edit form with current content
				setInputValue(actionData.originalContent || '')
				setCurrentStep({
					id: 'edit-paragraph-manual-input',
					message: 'Pas de tekst hieronder aan:',
					input: {
						type: 'textarea',
						placeholder: 'Voer hier je nieuwe tekst in...',
						defaultValue: actionData.originalContent,
					},
				})
				break
			case 'edit-paragraph-save-ai':
				// Save the AI-edited paragraph
				void fetcher.submit(
					{
						intent: 'update-paragraph',
						sectionId: actionData.sectionId,
						content: actionData.aiEditedContent,
					},
					{
						method: 'POST',
						action: '/resources/ai-assistant',
					},
				)
				setIsTyping(true)
				break
			case 'edit-paragraph-reject-ai':
				// Go back to asking what to change
				setCurrentStep({
					id: 'edit-paragraph-what',
					message: 'Wat wil je veranderen?',
					input: {
						type: 'text',
						placeholder: 'Beschrijf wat je wilt aanpassen...',
					},
				})
				break
			case 'edit-paragraph-save-manual':
				// This will be handled in handleTextSubmit
				break
			case 'edit-paragraph-cancel-manual':
				// Go back to options
				setCurrentStep({
					id: 'edit-paragraph-options',
					message: `Je wilt het volgende aanpassen: "${actionData.editInstructions}". Hoe wil je dit doen?`,
					options: [
						{ label: 'Ja graag', action: 'edit-paragraph-ai' },
						{
							label: 'Nee, ik wil de tekst zelf aanpassen',
							action: 'edit-paragraph-manual',
						},
					],
				})
				break
			case 'confirm-add-exhibition':
				// Call the exhibition flow to handle the creation
				handleExhibitionFlow('confirm-add-exhibition', '')
				break
			case 'confirm-remove-sculpture':
				// Delete the selected sculpture
				void fetcher.submit(
					{
						intent: 'delete-sculpture',
						sculptureId: actionData.sculptureId,
					},
					{
						method: 'POST',
						action: '/resources/ai-assistant',
					},
				)
				setIsTyping(true)
				// Clear remove mode
				setActionData((prev) => ({ ...prev, removeMode: false }))
				break
			case 'confirm-remove-current':
				// Delete the current sculpture
				if (params.title) {
					// Get the sculpture ID from the title/slug
					void fetcher.submit(
						{
							intent: 'delete-current-sculpture',
							sculptureSlug: params.title,
						},
						{
							method: 'POST',
							action: '/resources/ai-assistant',
						},
					)
					setIsTyping(true)
				}
				break
			case 'edit-exhibition-title':
				setCurrentStep({
					id: 'edit-exhibition-title-input',
					message: `Huidige titel: "${actionData.editingExhibition?.title}"\n\nVoer de nieuwe titel in:`,
					input: {
						type: 'text',
						placeholder: 'Nieuwe titel...',
						defaultValue: actionData.editingExhibition?.title || '',
					},
				})
				break
			case 'edit-exhibition-address':
				setCurrentStep({
					id: 'edit-exhibition-address-input',
					message: `Huidige adres: "${actionData.editingExhibition?.address || 'Geen adres'}"\n\nVoer het nieuwe adres in:`,
					input: {
						type: 'text',
						placeholder: 'Nieuw adres...',
						defaultValue: actionData.editingExhibition?.address || '',
					},
				})
				break
			case 'edit-exhibition-dates':
				setCurrentStep({
					id: 'edit-exhibition-dates-input',
					message: `Huidige datums:\nBegin: ${actionData.editingExhibition?.beginDate || 'Niet ingesteld'}\nEind: ${actionData.editingExhibition?.endDate || 'Niet ingesteld'}\n\nVoer de nieuwe begindatum in (YYYY-MM-DD):`,
					input: {
						type: 'text',
						placeholder: 'YYYY-MM-DD',
						defaultValue: actionData.editingExhibition?.beginDate || '',
					},
				})
				break
			case 'edit-exhibition-times':
				setCurrentStep({
					id: 'edit-exhibition-times-input',
					message: `Huidige openingstijden:\n${actionData.editingExhibition?.openingTimes ? (JSON.parse(actionData.editingExhibition.openingTimes) as string[]).join('\n') : 'Geen openingstijden'}\n\nVoer de nieuwe openingstijden in (één per regel):`,
					input: {
						type: 'textarea',
						placeholder: 'Ma-Vr: 10:00-17:00\nZa-Zo: 12:00-17:00',
						defaultValue: actionData.editingExhibition?.openingTimes ? (JSON.parse(actionData.editingExhibition.openingTimes) as string[]).join('\n') : '',
					},
				})
				break
			case 'edit-exhibition-links':
				setCurrentStep({
					id: 'edit-exhibition-website-input',
					message: `Huidige website: ${actionData.editingExhibition?.websiteUrl || 'Geen website'}\n\nVoer de nieuwe website URL in:`,
					input: {
						type: 'text',
						placeholder: 'https://...',
						defaultValue: actionData.editingExhibition?.websiteUrl || '',
					},
				})
				break
			case 'edit-exhibition-description':
				setCurrentStep({
					id: 'edit-exhibition-description-input',
					message: `Huidige beschrijving:\n"${actionData.editingExhibition?.description || 'Geen beschrijving'}"\n\nVoer de nieuwe beschrijving in:`,
					input: {
						type: 'textarea',
						placeholder: 'Nieuwe beschrijving...',
						defaultValue: actionData.editingExhibition?.description || '',
					},
				})
				break
			case 'save-exhibition-changes':
				// Save all the changes
				void fetcher.submit(
					{
						intent: 'save-exhibition-changes',
						exhibitionId: actionData.editingExhibition?.id,
						updates: JSON.stringify(actionData.exhibitionUpdates || {}),
					},
					{
						method: 'POST',
						action: '/resources/ai-assistant',
					},
				)
				setIsTyping(true)
				break
			case 'edit-exhibition-more':
				// Go back to the exhibition edit menu
				setCurrentStep({
					id: 'edit-exhibition-details',
					message: 'Welk veld wil je aanpassen?',
					options: [
						{ label: '📝 Titel aanpassen', action: 'edit-exhibition-title' },
						{ label: '📍 Adres aanpassen', action: 'edit-exhibition-address' },
						{ label: '📅 Datums aanpassen', action: 'edit-exhibition-dates' },
						{ label: '🕐 Openingstijden aanpassen', action: 'edit-exhibition-times' },
						{ label: '🔗 Links aanpassen', action: 'edit-exhibition-links' },
						{ label: '📄 Beschrijving aanpassen', action: 'edit-exhibition-description' },
						{ label: '💾 Wijzigingen opslaan', action: 'save-exhibition-changes' },
						{ label: '❌ Annuleren', action: 'close' },
					],
				})
				break
			case 'edit-sculpture':
				// Fetch all sculptures to show for editing
				void fetcher.submit(
					{
						intent: 'search-sculptures',
						query: '',
					},
					{
						method: 'POST',
						action: '/resources/ai-assistant',
					},
				)
				setIsTyping(true)
				// Set a flag to indicate we're in edit mode
				setActionData((prev) => ({ ...prev, editSculptureMode: true }))
				break
			case 'edit-sculpture-title':
				setCurrentStep({
					id: 'edit-sculpture-title-input',
					message: `Huidige titel: "${actionData.editingSculpture?.title}"\n\nVoer de nieuwe titel in:`,
					input: {
						type: 'text',
						placeholder: 'Nieuwe titel...',
						defaultValue: actionData.editingSculpture?.title || '',
					},
				})
				break
			case 'edit-sculpture-description':
				setCurrentStep({
					id: 'edit-sculpture-description-input',
					message: `Huidige beschrijving:\n"${actionData.editingSculpture?.content || 'Geen beschrijving'}"\n\nVoer de nieuwe beschrijving in:`,
					input: {
						type: 'textarea',
						placeholder: 'Nieuwe beschrijving...',
						defaultValue: actionData.editingSculpture?.content || '',
					},
				})
				break
			case 'edit-sculpture-category':
				setCurrentStep({
					id: 'edit-sculpture-category-select',
					message: `Huidige categorie: ${
						actionData.editingSculpture?.category === 'drijvend'
							? 'Drijvend'
							: actionData.editingSculpture?.category === 'openbare_ruimte'
								? 'Openbare ruimte'
								: 'Autonoom'
					}\n\nKies de nieuwe categorie:`,
					options: [
						{ label: '🎨 Autonoom', action: 'set-category-autonoom' },
						{ label: '🌊 Drijvend', action: 'set-category-drijvend' },
						{ label: '🏛️ Openbare ruimte', action: 'set-category-openbare-ruimte' },
					],
				})
				break
			case 'set-category-autonoom':
			case 'set-category-drijvend':
			case 'set-category-openbare-ruimte':
				// Store the category update
				const category =
					action === 'set-category-drijvend'
						? 'drijvend'
						: action === 'set-category-openbare-ruimte'
							? 'openbare_ruimte'
							: 'autonoom'
				void fetcher.submit(
					{
						intent: 'update-sculpture-field',
						sculptureId: actionData.editingSculpture?.id,
						field: 'category',
						value: category,
					},
					{
						method: 'POST',
						action: '/resources/ai-assistant',
					},
				)
				setIsTyping(true)
				break
			case 'save-sculpture-changes':
				// Save all the changes
				void fetcher.submit(
					{
						intent: 'save-sculpture-changes',
						sculptureId: actionData.editingSculpture?.id,
						updates: JSON.stringify(actionData.sculptureUpdates || {}),
					},
					{
						method: 'POST',
						action: '/resources/ai-assistant',
					},
				)
				setIsTyping(true)
				break
			case 'edit-sculpture-more':
				// Go back to the sculpture edit menu
				setCurrentStep({
					id: 'edit-sculpture-details',
					message: 'Welk veld wil je aanpassen?',
					options: [
						{ label: '📝 Titel aanpassen', action: 'edit-sculpture-title' },
						{ label: '📄 Beschrijving aanpassen', action: 'edit-sculpture-description' },
						{ label: '🏷️ Categorie aanpassen', action: 'edit-sculpture-category' },
						{ label: '🖼️ Afbeeldingen beheren', action: 'manage-sculpture-images' },
						{ label: '💾 Wijzigingen opslaan', action: 'save-sculpture-changes' },
						{ label: '❌ Annuleren', action: 'close' },
					],
				})
				break
			case 'manage-sculpture-images':
				// Get images for the sculpture being edited
				if (actionData.editingSculpture?.id) {
					void fetcher.submit(
						{
							intent: 'get-sculpture-images',
							sculptureId: actionData.editingSculpture.id,
						},
						{
							method: 'POST',
							action: '/resources/ai-assistant',
						},
					)
					setIsTyping(true)
				}
				break
			case 'delete-sculpture-image':
				// Ask which image to delete
				if (actionData.sculptureImages && actionData.sculptureImages.length > 0) {
					// Create custom options with image data
					const imageOptions = actionData.sculptureImages.map((img: any, index: number) => ({
						label: `${index + 1}. Afbeelding ${index + 1}`,
						action: 'select-image-to-delete' as AIAction,
						imageId: img.id, // Store image ID in option data
					}))
					setCurrentStep({
						id: 'select-image-to-delete',
						message: 'Welke afbeelding wil je verwijderen? Kies een nummer:',
						options: imageOptions as any,
					})
					setActionData((prev) => ({ ...prev, deleteImageMode: true, imageOptions }))
				}
				break
			case 'select-image-to-delete':
				// This case is handled by option click with imageId
				// The confirmation step is shown after the image is selected
				break
			case 'confirm-delete-image':
				// Delete the selected image
				if (actionData.selectedImageId) {
					void fetcher.submit(
						{
							intent: 'delete-sculpture-image',
							imageId: actionData.selectedImageId,
						},
						{
							method: 'POST',
							action: '/resources/ai-assistant',
						},
					)
					setIsTyping(true)
					setActionData((prev) => ({ ...prev, deleteImageMode: false, selectedImageId: null }))
				}
				break
			case 'reorder-sculpture-image-up':
			case 'reorder-sculpture-image-down':
				// Ask which image to reorder
				if (actionData.sculptureImages && actionData.sculptureImages.length > 1) {
					const direction = action === 'reorder-sculpture-image-up' ? 'omhoog' : 'omlaag'
					// Create custom options with image data
					const imageOptions = actionData.sculptureImages.map((img: any, index: number) => ({
						label: `${index + 1}. Afbeelding ${index + 1}`,
						action: 'select-image-to-reorder' as AIAction,
						imageId: img.id, // Store image ID in option data
					}))
					setCurrentStep({
						id: 'select-image-to-reorder',
						message: `Welke afbeelding wil je ${direction} verplaatsen? Kies een nummer:`,
						options: imageOptions as any,
					})
					setActionData((prev) => ({ 
						...prev, 
						reorderDirection: action === 'reorder-sculpture-image-up' ? 'up' : 'down',
						imageOptions
					}))
				}
				break
			case 'select-image-to-reorder':
				// Reorder the selected image
				if (actionData.selectedImageId && actionData.reorderDirection) {
					void fetcher.submit(
						{
							intent: 'reorder-sculpture-image',
							imageId: actionData.selectedImageId,
							direction: actionData.reorderDirection,
						},
						{
							method: 'POST',
							action: '/resources/ai-assistant',
						},
					)
					setIsTyping(true)
					setActionData((prev) => ({ ...prev, reorderDirection: null, selectedImageId: null }))
				}
				break
			case 'upload-sculpture-image':
				// For now, show a message that this needs to be done from the edit page
				setCurrentStep({
					id: 'upload-not-available',
					message: 'Het toevoegen van nieuwe afbeeldingen kan momenteel alleen via de bewerkpagina van het beeldhouwwerk. Wil je daar naartoe gaan?',
					options: [
						{ label: '👀 Ga naar bewerkpagina', action: 'view-sculpture' },
						{ label: '❌ Terug', action: 'manage-sculpture-images' },
					],
				})
				break
			case 'confirm-remove-exhibition':
				// Delete the selected exhibition
				void fetcher.submit(
					{
						intent: 'delete-exhibition',
						exhibitionId: actionData.exhibitionId,
					},
					{
						method: 'POST',
						action: '/resources/ai-assistant',
					},
				)
				setIsTyping(true)
				// Clear mode flags
				setActionData((prev) => ({
					...prev,
					editExhibitionMode: false,
					removeExhibitionMode: false,
				}))
				break
			default:
				break
		}
	}

	const handleTextSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		if (!inputValue.trim()) return

		// Store current step with user's input
		setConversationHistory((prev) => [
			...prev,
			{
				...currentStep,
				userResponse: inputValue,
			},
		])

		// Check if we're in a specific action flow
		if (currentStep.id?.startsWith('add-sculpture-')) {
			handleSculptureFlow(currentStep.id, inputValue)
		} else if (currentStep.id?.startsWith('add-photos-')) {
			handlePhotosFlow(currentStep.id, inputValue)
		} else if (currentStep.id?.startsWith('add-exhibition-')) {
			handleExhibitionFlow(currentStep.id, inputValue)
		} else if (currentStep.id?.startsWith('edit-exhibition-')) {
			// Handle exhibition field editing
			let field = ''
			let value = inputValue
			
			switch (currentStep.id) {
				case 'edit-exhibition-title-input':
					field = 'title'
					break
				case 'edit-exhibition-address-input':
					field = 'address'
					break
				case 'edit-exhibition-dates-input':
					// This is the begin date, now ask for end date
					setActionData((prev) => ({
						...prev,
						exhibitionUpdates: {
							...prev.exhibitionUpdates,
							beginDate: inputValue,
						},
					}))
					setCurrentStep({
						id: 'edit-exhibition-enddate-input',
						message: 'Voer de nieuwe einddatum in (YYYY-MM-DD, laat leeg voor geen einddatum):',
						input: {
							type: 'text',
							placeholder: 'YYYY-MM-DD',
							defaultValue: actionData.editingExhibition?.endDate || '',
						},
					})
					setInputValue('')
					return
				case 'edit-exhibition-enddate-input':
					field = 'endDate'
					break
				case 'edit-exhibition-times-input':
					field = 'openingTimes'
					value = JSON.stringify(inputValue.split('\n').filter(line => line.trim()))
					break
				case 'edit-exhibition-website-input':
					field = 'websiteUrl'
					// Now ask for Google Maps URL
					setActionData((prev) => ({
						...prev,
						exhibitionUpdates: {
							...prev.exhibitionUpdates,
							websiteUrl: inputValue,
						},
					}))
					setCurrentStep({
						id: 'edit-exhibition-maps-input',
						message: `Huidige Google Maps URL: ${actionData.editingExhibition?.googleMapsUrl || 'Geen Google Maps URL'}\n\nVoer de nieuwe Google Maps URL in:`,
						input: {
							type: 'text',
							placeholder: 'https://maps.google.com/...',
							defaultValue: actionData.editingExhibition?.googleMapsUrl || '',
						},
					})
					setInputValue('')
					return
				case 'edit-exhibition-maps-input':
					field = 'googleMapsUrl'
					break
				case 'edit-exhibition-description-input':
					field = 'description'
					break
			}
			
			if (field) {
				// Submit the field update
				void fetcher.submit(
					{
						intent: 'update-exhibition-field',
						exhibitionId: actionData.editingExhibition?.id,
						field,
						value,
					},
					{
						method: 'POST',
						action: '/resources/ai-assistant',
					},
				)
				setIsTyping(true)
			}
			setInputValue('')
		} else if (currentStep.id?.startsWith('edit-sculpture-')) {
			// Handle sculpture field editing
			let field = ''
			let value = inputValue
			
			switch (currentStep.id) {
				case 'edit-sculpture-title-input':
					field = 'title'
					break
				case 'edit-sculpture-description-input':
					field = 'content'
					break
			}
			
			if (field) {
				// Submit the field update
				void fetcher.submit(
					{
						intent: 'update-sculpture-field',
						sculptureId: actionData.editingSculpture?.id,
						field,
						value,
					},
					{
						method: 'POST',
						action: '/resources/ai-assistant',
					},
				)
				setIsTyping(true)
			}
			setInputValue('')
		} else if (
			currentStep.id === 'edit-paragraph-what' ||
			currentStep.id === 'edit-paragraph-confirm-yes'
		) {
			// Store the edit instructions
			setActionData((prev) => ({ ...prev, editInstructions: inputValue }))
			// Use AI to generate the new paragraph
			void fetcher.submit(
				{
					intent: 'ai-edit-paragraph',
					sectionId: actionData.sectionId,
					originalContent: actionData.originalContent,
					editInstructions: inputValue,
				},
				{
					method: 'POST',
					action: '/resources/ai-assistant',
				},
			)
			setIsTyping(true)
		} else if (currentStep.id === 'edit-paragraph-manual-input') {
			// Save the manually edited paragraph
			void fetcher.submit(
				{
					intent: 'update-paragraph',
					sectionId: actionData.sectionId,
					content: inputValue,
				},
				{
					method: 'POST',
					action: '/resources/ai-assistant',
				},
			)
			setIsTyping(true)
		} else {
			// Send to AI for analysis
			void fetcher.submit(
				{
					intent: 'analyze',
					message: inputValue,
				},
				{
					method: 'POST',
					action: '/resources/ai-assistant',
				},
			)
		}

		// Clear input and show typing
		setInputValue('')
		setIsTyping(true)
	}

	const stopRecordingAndTranscribe = useCallback(async () => {
		if (!audioRecorderRef.current || !isRecording) return

		// Clear silence timer
		if (silenceTimerRef.current) {
			clearTimeout(silenceTimerRef.current)
			silenceTimerRef.current = null
		}

		// Stop recording and transcribe
		const audioBlob = await audioRecorderRef.current.stopRecording()
		setIsRecording(false)
		setIsSilent(false)

		if (audioBlob && audioBlob.size > 0) {
			// Preserve state during processing
			preserveStateRef.current = true

			// Send audio to server for transcription
			const formData = new FormData()
			formData.append('intent', 'transcribe')
			formData.append('audio', audioBlob, 'recording.webm')

			// Show typing indicator while transcribing
			setIsTyping(true)

			void fetcher.submit(formData, {
				method: 'POST',
				action: '/resources/ai-assistant',
				encType: 'multipart/form-data',
			})
		}
	}, [isRecording, fetcher])

	const handleVoiceInput = useCallback(async () => {
		if (!audioRecorderRef.current) {
			return
		}

		if (isRecording) {
			await stopRecordingAndTranscribe()
		} else {
			// Start recording
			const started = await audioRecorderRef.current.startRecording()

			if (started) {
				setIsRecording(true)
				setIsSilent(false)

				// Start monitoring for silence after a brief delay
				// This gives time for user to start speaking
				setTimeout(() => {
					setIsSilent(true)
				}, 2000)
			} else {
				alert('Kan microfoon niet openen. Controleer je browser instellingen.')
			}
		}
	}, [isRecording, stopRecordingAndTranscribe])

	// Handle drawer close with confirmation
	const handleDrawerClose = (open: boolean) => {
		if (!open && hasUnsavedData()) {
			// Trying to close with unsaved data
			setPendingAction('close')
			setShowExitConfirmation(true)
		} else {
			setIsOpen(open)
			// Reset states when closing
			if (!open) {
				setActionData({})
				setCurrentStep({
					id: 'greeting',
					message: getGreeting(),
					options: defaultOptions,
				})
				setConversationHistory([])
			}
		}
	}

	// Handle confirmation dialog
	const handleConfirmExit = () => {
		setShowExitConfirmation(false)
		const action = pendingAction
		setPendingAction(null)
		
		// Clear data and execute the action
		setActionData({})
		if (action === 'close') {
			setIsOpen(false)
			setCurrentStep({
				id: 'greeting',
				message: getGreeting(),
				options: defaultOptions,
			})
			setConversationHistory([])
		} else if (action) {
			handleOptionClick(action)
		}
	}

	return (
		<>
			<AIAssistantButton onClick={() => setIsOpen(true)} />

			<Drawer open={isOpen} onOpenChange={handleDrawerClose}>
				<DrawerContent className="mx-auto flex max-h-[85vh] max-w-3xl flex-col overflow-hidden !rounded-3xl [&[data-vaul-drawer-direction=bottom]]:!right-[10px] [&[data-vaul-drawer-direction=bottom]]:!bottom-[20px] [&[data-vaul-drawer-direction=bottom]]:!left-[10px] [&[data-vaul-drawer-direction=bottom]]:!rounded-t-3xl">
					{/* Info button in top right */}
					<div className="absolute top-12 right-4 z-10">
						<Button
							variant="ghost"
							size="icon"
							className="h-8 w-8 rounded-full text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
							onClick={() => {
								// Stop any ongoing speech
								if (voiceAssistantRef.current) {
									voiceAssistantRef.current.stopSpeaking()
								}
								setIsSpeaking(false)

								// Store current step before showing info
								setConversationHistory((prev) => [...prev, currentStep])
								setCurrentStep({
									id: 'info',
									message:
										"Hoi, ik ben Eva Yinèk, jouw AI-assistent.\n\nIk help je met het beheren van je website. Zo kun je bijvoorbeeld:\n\n• Nieuwe beeldhouwwerken toevoegen\n• Foto's uploaden bij je werken\n• Tentoonstellingen beheren\n• De voorpagina aanpassen\n\nJe kunt me vragen stellen door te typen of de microfoon te gebruiken.",
								})
							}}
							title="Info"
						>
							<svg
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
								className="h-6 w-6"
							>
								<circle cx="12" cy="12" r="10" />
								<line x1="12" y1="16" x2="12" y2="12" />
								<line x1="12" y1="8" x2="12.01" y2="8" />
							</svg>
						</Button>
					</div>

					{!navigator.onLine && (
						<div className="px-4 pt-2">
							<p className="text-xs text-amber-600">
								⚠️ AI Assistent vereist een internetverbinding
							</p>
						</div>
					)}

					<div className="flex-1 overflow-y-auto px-4 pt-6 pb-2">
						<AnimatePresence mode="wait">
							<motion.div
								key={currentStep.id}
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -10 }}
								transition={{ duration: 0.2 }}
								className="space-y-4"
							>
								{/* AI Message */}
								<div className="flex items-start gap-3">
									<div className="flex size-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600">
										<svg
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
											className="size-4 text-white"
										>
											<path d="M12 2v20" />
											<path d="M8 7v10" />
											<path d="M4 10v4" />
											<path d="M16 7v10" />
											<path d="M20 10v4" />
										</svg>
									</div>
									<div className="flex-1">
										<div className="flex items-start gap-2">
											<p className="flex-1 text-xl whitespace-pre-line text-gray-800 dark:text-gray-200">
												{currentStep.message}
											</p>
											{/* Audio cache indicator - only show in development */}
											{typeof window !== 'undefined' &&
												(window.location.hostname === 'localhost' ||
													window.location.hostname === '127.0.0.1') &&
												audioFromCache !== null && (
													<motion.div
														initial={{ scale: 0, opacity: 0 }}
														animate={{ scale: 1, opacity: 1 }}
														exit={{ scale: 0, opacity: 0 }}
														className={cn(
															'flex size-6 flex-shrink-0 items-center justify-center rounded-full',
															audioFromCache ? 'bg-green-500' : 'bg-blue-500',
														)}
														title={
															audioFromCache
																? 'Audio from cache'
																: 'Audio generated live'
														}
													>
														{audioFromCache ? (
															// Cache icon (database)
															<svg
																viewBox="0 0 24 24"
																fill="none"
																stroke="currentColor"
																strokeWidth="2"
																strokeLinecap="round"
																strokeLinejoin="round"
																className="size-3 text-white"
															>
																<ellipse cx="12" cy="5" rx="9" ry="3" />
																<path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
																<path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
															</svg>
														) : (
															// Live icon (zap)
															<svg
																viewBox="0 0 24 24"
																fill="none"
																stroke="currentColor"
																strokeWidth="2"
																strokeLinecap="round"
																strokeLinejoin="round"
																className="size-3 text-white"
															>
																<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
															</svg>
														)}
													</motion.div>
												)}
										</div>
										{/* ULTIMATE: Optimistic action indicator */}
										<AnimatePresence>
											{optimisticAction && (
												<motion.div
													initial={{ opacity: 0, y: -10 }}
													animate={{ opacity: 1, y: 0 }}
													exit={{ opacity: 0, y: -10 }}
													className="mt-2 flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400"
												>
													<motion.div
														className="size-4"
														animate={{ rotate: 360 }}
														transition={{
															duration: 1,
															repeat: Infinity,
															ease: 'linear',
														}}
													>
														<svg
															viewBox="0 0 24 24"
															fill="none"
															stroke="currentColor"
															strokeWidth="2"
															strokeLinecap="round"
															strokeLinejoin="round"
															className="size-4"
														>
															<path d="M21 12a9 9 0 1 1-6.219-8.56" />
														</svg>
													</motion.div>
													<span>{optimisticAction.message}</span>
												</motion.div>
											)}
										</AnimatePresence>
										{(isTyping || isSpeaking) && !optimisticAction && (
											<motion.div
												className="mt-2 flex gap-1"
												initial={{ opacity: 0 }}
												animate={{ opacity: 1 }}
											>
												{[0, 1, 2].map((i) => (
													<motion.div
														key={i}
														className={cn(
															'size-2 rounded-full',
															isSpeaking ? 'bg-blue-500' : 'bg-gray-400',
														)}
														animate={{
															scale: [1, 1.2, 1],
															opacity: [0.5, 1, 0.5],
														}}
														transition={{
															duration: isSpeaking ? 0.5 : 1,
															repeat: Infinity,
															delay: i * 0.2,
														}}
													/>
												))}
											</motion.div>
										)}
									</div>
								</div>

								{/* Options */}
								{!isTyping && currentStep.options && (
									<div className="mt-4 mr-11 ml-11 grid grid-cols-1 gap-2 sm:grid-cols-2">
										{currentStep.options.map((option, index) => (
											<motion.div
												key={option.label}
												initial={{ opacity: 0, x: -10 }}
												animate={{ opacity: 1, x: 0 }}
												transition={{ delay: index * 0.1 }}
											>
												<Button
													variant="outline"
													className="w-full justify-start text-left"
													onClick={() => handleOptionClick(option.action, (option as any).imageId)}
												>
													{option.label}
												</Button>
											</motion.div>
										))}
									</div>
								)}

								{/* Input field in main content area */}
								{!isTyping &&
									currentStep.input &&
									currentStep.input.type !== 'file' && (
										<motion.div
											initial={{ opacity: 0, y: 10 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{ delay: 0.2 }}
											className={cn(
												'mt-4 mr-11 ml-11',
												showTextInput && 'mb-8',
											)}
										>
											<form onSubmit={handleTextSubmit}>
												<div className="flex flex-col gap-2">
													{currentStep.input.type === 'textarea' ? (
														<>
															<Textarea
																placeholder={currentStep.input.placeholder}
																value={
																	inputValue ||
																	currentStep.input.defaultValue ||
																	''
																}
																onChange={(e) => setInputValue(e.target.value)}
																className="min-h-[200px]"
																autoFocus
															/>
															<div className="flex justify-end gap-2">
																<Button
																	type="button"
																	variant="outline"
																	onClick={() =>
																		handleOptionClick(
																			'edit-paragraph-cancel-manual',
																		)
																	}
																>
																	Annuleren
																</Button>
																<Button
																	type="submit"
																	disabled={
																		!inputValue.trim() ||
																		fetcher.state !== 'idle'
																	}
																>
																	Opslaan
																</Button>
															</div>
														</>
													) : (
														<div className="flex gap-2">
															<Input
																ref={inputRef}
																type={
																	currentStep.input.type === 'text'
																		? 'text'
																		: 'search'
																}
																placeholder={currentStep.input.placeholder}
																value={inputValue}
																onChange={(e) => setInputValue(e.target.value)}
																className="flex-1"
																autoFocus
															/>
															<Button
																type="submit"
																disabled={
																	!inputValue.trim() || fetcher.state !== 'idle'
																}
															>
																Verstuur
															</Button>
														</div>
													)}
												</div>
											</form>
										</motion.div>
									)}

								{/* Display sculptures */}
								{!isTyping && currentStep.sculptures && (
									<motion.div
										initial={{ opacity: 0, y: 10 }}
										animate={{ opacity: 1, y: 0 }}
										transition={{ delay: 0.2 }}
										className="mt-4"
									>
										<SculptureSelector
											sculptures={currentStep.sculptures}
											currentSculpture={
												actionData.sculptureId
													? currentStep.sculptures.find(
															(s) => s.id === actionData.sculptureId,
														)
													: null
											}
											onSelect={(sculpture) => {
												// Stop any ongoing speech
												if (voiceAssistantRef.current) {
													voiceAssistantRef.current.stopSpeaking()
												}
												setIsSpeaking(false)

												setActionData((prev) => ({
													...prev,
													sculptureId: sculpture.id,
													sculptureTitle: sculpture.title,
												}))

												// Check if we're in remove mode
												if (actionData.removeMode) {
													setCurrentStep({
														id: 'confirm-remove-sculpture',
														message: `Weet je zeker dat je "${sculpture.title}" wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`,
														options: [
															{
																label: '🗑️ Ja, verwijder dit beeld',
																action: 'confirm-remove-sculpture' as AIAction,
															},
															{ label: '❌ Nee, annuleren', action: 'close' },
														],
													})
												} else if (actionData.editSculptureMode) {
													// Fetch sculpture details for editing
													void fetcher.submit(
														{
															intent: 'get-sculpture-details',
															sculptureId: sculpture.id,
														},
														{
															method: 'POST',
															action: '/resources/ai-assistant',
														},
													)
													setIsTyping(true)
												} else {
													setCurrentStep({
														id: 'photos-for-sculpture',
														message: `Je hebt "${sculpture.title}" geselecteerd. Hoeveel foto's wil je toevoegen?`,
														options: [
															{ label: '1 foto', action: 'add-1-photo' },
															{ label: "3 foto's", action: 'add-3-photos' },
															{ label: "5 foto's", action: 'add-5-photos' },
														],
													})
												}
											}}
											onVoiceInput={handleVoiceInput}
											isRecording={isRecording}
											mode={actionData.removeMode ? 'remove' : actionData.editSculptureMode ? 'edit' : 'add-photos'}
										/>
									</motion.div>
								)}

								{/* Display exhibitions */}
								{!isTyping && currentStep.exhibitions && (
									<div className="mt-4 mr-11 ml-11">
										<div className="grid grid-cols-1 gap-4">
											{currentStep.exhibitions.map((exhibition, index) => (
												<motion.div
													key={exhibition.id}
													initial={{ opacity: 0, y: 10 }}
													animate={{ opacity: 1, y: 0 }}
													transition={{ delay: index * 0.05 }}
													className="group cursor-pointer rounded-lg border p-4 transition-all duration-300 ease-out hover:shadow-lg"
													onClick={() => {
														// Stop any ongoing speech
														if (voiceAssistantRef.current) {
															voiceAssistantRef.current.stopSpeaking()
														}
														setIsSpeaking(false)

														setActionData((prev) => ({
															...prev,
															exhibitionId: exhibition.id,
															exhibitionTitle: exhibition.title,
														}))

														// Check mode
														if (actionData.editExhibitionMode) {
															// Fetch exhibition details for editing
															void fetcher.submit(
																{
																	intent: 'get-exhibition-details',
																	exhibitionId: exhibition.id,
																},
																{
																	method: 'POST',
																	action: '/resources/ai-assistant',
																},
															)
															setIsTyping(true)
														} else if (actionData.removeExhibitionMode) {
															setCurrentStep({
																id: 'confirm-remove-exhibition',
																message: `Weet je zeker dat je "${exhibition.title}" wilt verwijderen?`,
																options: [
																	{
																		label: '🗑️ Ja, verwijder',
																		action:
																			'confirm-remove-exhibition' as AIAction,
																	},
																	{
																		label: '❌ Nee, annuleren',
																		action: 'close',
																	},
																],
															})
														}
													}}
												>
													<div className="flex items-center justify-between">
														<div>
															<h3 className="font-semibold">
																{exhibition.title}
															</h3>
															{exhibition.address && (
																<p className="text-sm text-gray-600">
																	{exhibition.address}
																</p>
															)}
															<p className="text-sm text-gray-500">
																{exhibition.type === 'gallery'
																	? 'Galerie'
																	: exhibition.isCurrent
																		? 'Huidige expositie'
																		: 'Afgelopen expositie'}
															</p>
														</div>
														<div className="text-3xl">
															{exhibition.type === 'gallery' ? '🏛️' : '🎨'}
														</div>
													</div>
												</motion.div>
											))}
										</div>
									</div>
								)}

								{/* Photo upload UI */}
								{!isTyping &&
									currentStep.id === 'upload-photos' &&
									actionData.photoCount && (
										<motion.div
											initial={{ opacity: 0, y: 10 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{ delay: 0.2 }}
											className="mt-4 mr-11 ml-11"
										>
											<AIPhotoUpload
												photoCount={actionData.photoCount}
												sculptureTitle={
													actionData.sculptureTitle || 'het beeldhouwwerk'
												}
												onSubmit={(files) => {
													// Submit the files to the server
													const formData = new FormData()
													formData.append('intent', 'upload-photos')
													formData.append('sculptureId', actionData.sculptureId)
													files.forEach((file, index) => {
														formData.append(`photo-${index}`, file)
													})

													void fetcher.submit(formData, {
														method: 'POST',
														action: '/resources/ai-assistant',
														encType: 'multipart/form-data',
													})

													setIsTyping(true)
												}}
												onCancel={() => {
													// Go back to the previous step
													setCurrentStep({
														id: 'photos-for-sculpture',
														message: `Je hebt "${actionData.sculptureTitle}" geselecteerd. Hoeveel foto's wil je toevoegen?`,
														options: [
															{ label: '1 foto', action: 'add-1-photo' },
															{ label: "3 foto's", action: 'add-3-photos' },
															{ label: "5 foto's", action: 'add-5-photos' },
														],
													})
												}}
											/>
										</motion.div>
									)}

								{/* Homepage image selector UI */}
								{!isTyping &&
									currentStep.id === 'select-homepage-image' &&
									currentStep.homepageImageData && (
										<motion.div
											initial={{ opacity: 0, y: 10 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{ delay: 0.2 }}
											className="mt-4"
										>
											<HomepageImageSelector
												currentImage={
													currentStep.homepageImageData.currentImage
												}
												allSculptures={
													currentStep.homepageImageData.allSculptures || []
												}
												positionName={actionData.positionName || ''}
												onSelect={(sculptureId) => {
													// Submit the selection to the server
													void fetcher.submit(
														{
															intent: 'update-homepage-image',
															positionIndex:
																actionData.positionIndex?.toString() || '0',
															sculptureId,
														},
														{
															method: 'POST',
															action: '/resources/ai-assistant',
														},
													)
													setIsTyping(true)
												}}
												onCancel={() => {
													// Go back to position selection
													setCurrentStep({
														id: 'select-homepage-position',
														message:
															'De voorpagina bestaat uit 5 fragmenten en één centraal beeld in het midden getoond achter de woorden Fragments of Homo Ludens. Welk beeld wil je vervangen?',
														options: [
															{
																label: 'Linksboven',
																action: 'select-position-linksboven',
															},
															{
																label: 'Rechtsboven',
																action: 'select-position-rechtsboven',
															},
															{
																label: 'Linksonder',
																action: 'select-position-linksonder',
															},
															{
																label: 'Middenonder',
																action: 'select-position-middenonder',
															},
															{
																label: 'Rechtsonder',
																action: 'select-position-rechtsonder',
															},
															{
																label: 'Het centrale beeld',
																action: 'select-position-centraal',
															},
														],
													})
												}}
											/>
										</motion.div>
									)}
							</motion.div>
						</AnimatePresence>
					</div>

					<DrawerFooter className="relative">
						{/* Text input mode - show keyboard/mic toggle and back button when there's input in main content */}
						{showTextInput &&
							currentStep.input &&
							currentStep.input.type !== 'file' && (
								<div className="absolute right-4 bottom-4 left-4 flex items-center justify-between">
									<Button
										variant="ghost"
										size="icon"
										onClick={() => setShowTextInput(false)}
										className="h-10 w-10"
										title="Schakel naar spraak"
									>
										<svg
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
											className="h-5 w-5"
										>
											<path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
											<path d="M19 10v2a7 7 0 0 1-14 0v-2" />
											<line x1="12" y1="19" x2="12" y2="23" />
											<line x1="8" y1="23" x2="16" y2="23" />
										</svg>
									</Button>

									{/* Back button */}
									{conversationHistory.length > 0 && (
										<Button
											variant="ghost"
											size="sm"
											onClick={() => {
												// Stop any ongoing speech
												if (voiceAssistantRef.current) {
													voiceAssistantRef.current.stopSpeaking()
												}
												setIsSpeaking(false)

												const previousStep =
													conversationHistory[conversationHistory.length - 1]
												if (previousStep) {
													setCurrentStep(previousStep)
													setConversationHistory((prev) => prev.slice(0, -1))
													setShowTextInput(false)
												}
											}}
											className="ml-auto"
										>
											Terug
										</Button>
									)}
								</div>
							)}

						{/* Text input mode - only show in footer if NOT already shown in main content */}
						{showTextInput && !currentStep.input && (
							<div className="flex w-full items-center gap-2">
								{/* Mic toggle button */}
								<Button
									variant="ghost"
									size="icon"
									onClick={() => setShowTextInput(false)}
									className="h-10 w-10 flex-shrink-0"
									title="Schakel naar spraak"
								>
									<svg
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
										className="h-5 w-5"
									>
										<path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
										<path d="M19 10v2a7 7 0 0 1-14 0v-2" />
										<line x1="12" y1="19" x2="12" y2="23" />
										<line x1="8" y1="23" x2="16" y2="23" />
									</svg>
								</Button>

								{/* Input form */}
								<form onSubmit={handleTextSubmit} className="flex flex-1 gap-2">
									<Input
										ref={inputRef}
										type="text"
										placeholder="Type hier je vraag..."
										value={inputValue}
										onChange={(e) => setInputValue(e.target.value)}
										className="flex-1"
										autoFocus
									/>
									<Button
										type="submit"
										disabled={!inputValue.trim() || fetcher.state !== 'idle'}
									>
										Verstuur
									</Button>
								</form>

								{/* Back button */}
								{conversationHistory.length > 0 && (
									<Button
										variant="ghost"
										size="sm"
										onClick={() => {
											// Stop any ongoing speech
											if (voiceAssistantRef.current) {
												voiceAssistantRef.current.stopSpeaking()
											}
											setIsSpeaking(false)

											const previousStep =
												conversationHistory[conversationHistory.length - 1]
											if (previousStep) {
												setCurrentStep(previousStep)
												setConversationHistory((prev) => prev.slice(0, -1))
												setShowTextInput(false)
											}
										}}
										className="flex-shrink-0"
									>
										Terug
									</Button>
								)}
							</div>
						)}

						{/* Voice input mode */}
						{!showTextInput && (
							<div className="flex w-full items-center justify-center py-4">
								<motion.button
									onClick={handleVoiceInput}
									className={cn(
										'relative flex h-20 w-20 items-center justify-center rounded-full transition-all',
										isRecording
											? 'bg-red-500 hover:bg-red-600'
											: 'bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600',
									)}
									whileHover={{ scale: 1.05 }}
									whileTap={{ scale: 0.95 }}
								>
									{/* Recording animation */}
									{isRecording && (
										<>
											{/* ULTIMATE: Audio level responsive pulse */}
											<motion.div
												className="absolute inset-0 rounded-full bg-red-500"
												animate={{
													scale: 1 + audioLevel.level * 0.4,
													opacity: 0.3 + audioLevel.level * 0.4,
												}}
												transition={{
													duration: 0.1,
													ease: 'easeOut',
												}}
											/>
											{/* ULTIMATE: Peak level indicator ring */}
											<motion.div
												className="absolute inset-0 rounded-full border-2 border-red-300"
												animate={{
													scale: 1 + audioLevel.peak * 0.5,
													opacity: audioLevel.peak * 0.6,
												}}
												transition={{
													duration: 0.15,
													ease: 'easeOut',
												}}
											/>
											{/* ULTIMATE: Audio wave animation using real audio levels */}
											{!isSilent && (
												<div className="absolute inset-0 flex items-center justify-center">
													{audioLevel.history.length > 0
														? audioLevel.history.slice(-5).map((level, i) => (
																<motion.div
																	key={i}
																	className="mx-0.5 w-1 bg-white"
																	animate={{
																		height: `${12 + level * 16}px`,
																	}}
																	transition={{
																		duration: 0.1,
																		ease: 'easeOut',
																	}}
																/>
															))
														: [...Array(5)].map((_, i) => (
																<motion.div
																	key={i}
																	className="mx-0.5 h-3 w-1 bg-white"
																	animate={{
																		height: ['12px', '24px', '12px'],
																	}}
																	transition={{
																		duration: 0.5,
																		repeat: Infinity,
																		delay: i * 0.1,
																	}}
																/>
															))}
												</div>
											)}
											{/* Send arrow when silent */}
											{isSilent && (
												<motion.svg
													initial={{ opacity: 0, scale: 0.5 }}
													animate={{ opacity: 1, scale: 1 }}
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
													className="h-8 w-8 text-white"
												>
													<line x1="12" y1="19" x2="12" y2="5" />
													<polyline points="5 12 12 5 19 12" />
												</motion.svg>
											)}
										</>
									)}

									{/* Microphone icon when not recording */}
									{!isRecording && (
										<svg
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
											className="h-8 w-8"
										>
											<path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
											<path d="M19 10v2a7 7 0 0 1-14 0v-2" />
											<line x1="12" y1="19" x2="12" y2="23" />
											<line x1="8" y1="23" x2="16" y2="23" />
										</svg>
									)}
								</motion.button>
							</div>
						)}

						{/* Voice input mode controls */}
						{!showTextInput && (
							<div className="absolute right-4 bottom-4 left-4 flex items-center justify-between">
								{/* Keyboard toggle */}
								<Button
									variant="ghost"
									size="icon"
									onClick={() => setShowTextInput(true)}
									className="h-10 w-10"
									title="Schakel naar toetsenbord"
								>
									<svg
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
										className="h-5 w-5"
									>
										<rect x="2" y="6" width="20" height="12" rx="2" />
										<line x1="6" y1="10" x2="6" y2="10" />
										<line x1="10" y1="10" x2="10" y2="10" />
										<line x1="14" y1="10" x2="14" y2="10" />
										<line x1="18" y1="10" x2="18" y2="10" />
										<line x1="6" y1="14" x2="6" y2="14" />
										<line x1="10" y1="14" x2="10" y2="14" />
										<line x1="14" y1="14" x2="14" y2="14" />
										<line x1="18" y1="14" x2="18" y2="14" />
										<line x1="8" y1="22" x2="16" y2="22" />
									</svg>
								</Button>

								{/* Back button */}
								{conversationHistory.length > 0 && (
									<Button
										variant="ghost"
										size="sm"
										onClick={() => {
											// Stop any ongoing speech
											if (voiceAssistantRef.current) {
												voiceAssistantRef.current.stopSpeaking()
											}
											setIsSpeaking(false)

											const previousStep =
												conversationHistory[conversationHistory.length - 1]
											if (previousStep) {
												setCurrentStep(previousStep)
												setConversationHistory((prev) => prev.slice(0, -1))
												setShowTextInput(false)
											}
										}}
										className="ml-auto"
									>
										Terug
									</Button>
								)}
							</div>
						)}
					</DrawerFooter>
				</DrawerContent>
			</Drawer>

			{/* Celebration Animation */}
			<CelebrationAnimation
				isVisible={showCelebration}
				onComplete={() => setShowCelebration(false)}
			/>
			
			{/* Exit Confirmation Dialog */}
			<Dialog open={showExitConfirmation} onOpenChange={setShowExitConfirmation}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<Icon
								name="question-mark-circled"
								className="text-destructive h-5 w-5"
							/>
							Weet je het zeker?
						</DialogTitle>
						<DialogDescription>
							{pendingAction === 'close' 
								? "Je hebt nog niet-opgeslagen wijzigingen. Als je nu sluit, gaan deze verloren."
								: "Je hebt nog niet-opgeslagen wijzigingen in je huidige actie. Als je nu naar een andere actie gaat, gaan deze verloren."}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button 
							variant="outline" 
							onClick={() => {
								setShowExitConfirmation(false)
								setPendingAction(null)
							}}
						>
							Nee, blijf hier
						</Button>
						<Button 
							variant="destructive" 
							onClick={handleConfirmExit}
						>
							{pendingAction === 'close' ? "Ja, sluit af" : "Ja, ga door"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	)
}
